import React, { useState, useEffect, useRef } from 'react';
import cloudApi from '../../../services/cloudApi';
import { HiOutlineCheck, HiOutlineChevronDown, HiOutlineClock } from 'react-icons/hi2';

const ExtractionHistoryDropdown = ({
  taskId,
  currentTableId,
  onTableSelect,
  isDarkMode = true,
  relativeTime = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const dropdownRef = useRef(null);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Load tables when dropdown opens (lazy load)
  useEffect(() => {
    if (isOpen && !hasLoaded && taskId) {
      loadTables();
    }
  }, [isOpen, taskId, hasLoaded]);

  const loadTables = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await cloudApi.getTablesByTaskId(taskId, { limit: 50 });
      setTables(response.data || []);
      setHasLoaded(true);
    } catch (err) {
      console.error('Failed to load extraction history:', err);
      setError(err.message || 'Failed to load extraction history');
    } finally {
      setLoading(false);
    }
  };

  const handleTableClick = (tableId) => {
    if (tableId !== currentTableId) {
      onTableSelect(tableId);
    }
    setIsOpen(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = cloudApi.toLocalDate(dateString);
    if (!date) return '';

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Don't render if no taskId
  if (!taskId) return null;

  return (
    <div className="relative flex-shrink-0 group/history" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-8 h-8 flex items-center justify-center rounded-md transition-all duration-150 ${
          isOpen
            ? isDarkMode
              ? "bg-white/[0.1] text-white"
              : "bg-white text-gray-900 shadow-sm"
            : isDarkMode
              ? "text-gray-500 hover:text-gray-300 hover:bg-white/[0.06]"
              : "text-gray-400 hover:text-gray-600 hover:bg-white"
        }`}
      >
        <HiOutlineClock className="w-4 h-4" />
      </button>

      {/* Custom tooltip */}
      {!isOpen && (
        <div className={`absolute right-0 top-full mt-2 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap opacity-0 invisible group-hover/history:opacity-100 group-hover/history:visible transition-all duration-200 pointer-events-none z-50 ${
          isDarkMode
            ? "bg-[#1a1a1c] text-gray-200 border border-white/[0.08] shadow-xl"
            : "bg-white text-gray-700 border border-gray-200 shadow-lg"
        }`}>
          <div className="flex items-center gap-2">
            <span className={isDarkMode ? "text-gray-500" : "text-gray-400"}>Extracted</span>
            <span>{relativeTime || "—"}</span>
          </div>
          <div className={`text-2xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
            Click to view history
          </div>
        </div>
      )}

      {/* Dropdown Panel */}
      {isOpen && (
        <div className={`absolute right-0 top-full mt-1.5 w-72 rounded-xl shadow-2xl z-[100] overflow-hidden border ${
          isDarkMode
            ? "bg-[#1a1a1c] border-white/[0.08]"
            : "bg-white border-gray-200"
        }`}>
          {/* Header */}
          <div className={`px-3 py-2.5 border-b ${isDarkMode ? "border-white/[0.06]" : "border-gray-100"}`}>
            <h4 className={`text-xs font-medium ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>Extraction History</h4>
            <p className={`text-2xs mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>Select a previous extraction</p>
          </div>

          {/* Content */}
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <div className={`animate-spin rounded-full h-4 w-4 border-2 border-t-transparent ${
                  isDarkMode ? "border-gray-700 border-t-gray-400" : "border-gray-200 border-t-gray-500"
                }`}></div>
              </div>
            ) : error ? (
              <div className="text-center py-4 px-3">
                <div className="text-red-400/80 text-xs mb-2">{error}</div>
                <button
                  onClick={loadTables}
                  className={`text-xs transition-colors ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-700"}`}
                >
                  Retry
                </button>
              </div>
            ) : tables.length === 0 ? (
              <div className={`text-center py-4 text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                No extraction history available
              </div>
            ) : tables.length === 1 ? (
              <div className={`text-center py-4 text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                This is the only extraction
              </div>
            ) : (
              <div className="py-1">
                {tables.map((table) => {
                  const isCurrent = table.id === currentTableId;

                  return (
                    <button
                      key={table.id}
                      onClick={() => handleTableClick(table.id)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 text-left transition-colors ${
                        isCurrent
                          ? isDarkMode ? 'bg-white/[0.06]' : 'bg-gray-100'
                          : isDarkMode ? 'hover:bg-white/[0.04]' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <span className={`text-xs font-medium ${
                          isCurrent
                            ? isDarkMode ? 'text-white' : 'text-gray-900'
                            : isDarkMode ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          {formatDate(table.createdAt)}
                        </span>
                        <span className={`text-2xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                          {(table.rowCount || 0).toLocaleString()} rows
                        </span>
                      </div>

                      {isCurrent && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                          <span className={`text-2xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Current</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExtractionHistoryDropdown;
